import moment, { type Moment } from 'moment';
import 'moment-timezone';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import 'moment/locale/de-at';
import 'moment/locale/de-ch';
import 'moment/locale/fr-ch';
import 'moment/locale/fr'; // the last imported locale seems to be the default one if none is set.

import { getI18nLocale, type Locale } from '@front/i18n';

import { WEEK_DAYS } from '../constants/date';
import type { Hour, Weekday } from '../types/commons';

/**
 * @see {@link packages/common/src/utils/country.ts} where :
 * - moment locale
 * - moment timzone
 * are set.
 */
export default moment;

export const calculateAge = (value: Moment) => moment().diff(value, 'years');

export const getMoment = (day: string, hourMinutes: Hour) =>
  moment(day).set({ ...hourMinutes, second: 0, millisecond: 0 });

export const getWeekDay = (appointment?: string) =>
  appointment ? WEEK_DAYS.find((wd) => wd.index === moment(appointment).day()) : undefined;

export const getMin = (first: Moment | undefined, second: Moment) => (first ? moment.min(first, second) : second);

export const getMax = (first: Moment | undefined, second: Moment) => (first ? moment.max(first, second) : second);

export const fromDateAndTime = (date: Moment, time: Moment) =>
  moment({
    year: date.year(),
    month: date.month(),
    date: date.date(),
    hour: time.hour(),
    minute: time.minute(),
  });

export const isInTheFuture = (date?: Moment | string | null) => !!date && moment(date).isAfter(moment());

export const isWithin48Hours = (time: Moment) => {
  const duration = moment.duration(time.diff(moment()));

  return (
    duration.asSeconds() >= 0 && // to make sure time is in the future
    duration <= moment.duration(48, 'hour')
  );
};

export const getLocaleWeekdays = (): string[] => moment.weekdays(true);

export const getLocaleWeekdayNumbers = (): Weekday[] =>
  getLocaleWeekdays().map((weekday) => Number(moment(weekday, 'dddd').format('d')) as Weekday);

export const getDayName = (weekday: Weekday): string => moment.weekdays(weekday);

export const sortAscending = (moments: Moment[]) => [...moments.sort((a: Moment, b: Moment) => a.diff(b))];

const DEFAULT_LOCALES: Partial<Record<Locale, string>> = {
  en: 'en-gb',
};

// We specifically provide 'gb' country whenever we detect locale as 'en'
// so moment.weekdays(true) can return a correctly sorted list (based on country) for 'en' users
export const setMomentLocale = () => {
  const locale = getI18nLocale();

  moment.locale(DEFAULT_LOCALES[locale] || locale.toLowerCase());
};

export const areMomentArraysEqual = (arr1: Moment[], arr2: Moment[]): boolean =>
  arr1.length === arr2.length &&
  arr1.sort() &&
  arr2.sort() &&
  arr1.every((moment1, index) => moment1.isSame(arr2[index]));

type GetDateBetweenParams = {
  start: Moment;
  end: Moment;
  options?: {
    includeLast?: boolean;
    unit?: moment.unitOfTime.DurationConstructor;
  };
};

export const getDatesBetween = ({
  start,
  end,
  options,
  options: { includeLast = true, unit = 'day' } = {},
}: GetDateBetweenParams): Moment[] => {
  if (start.isAfter(end, unit)) return [];
  if (start.isSame(end, unit)) return includeLast ? [start] : [];

  return [start].concat(
    getDatesBetween({
      start: moment(start).add(1, unit).startOf(unit),
      end,
      options,
    }),
  );
};

export const findClosest = <T extends string | Moment>(
  date: string | Moment,
  dates: T[],
  direction: 'previous' | 'next',
): T | null => {
  const comparisonFn: Record<typeof direction, 'isSameOrAfter' | 'isSameOrBefore'> = {
    previous: 'isSameOrBefore',
    next: 'isSameOrAfter',
  };

  return dates.reduce<T | null>((acc, cur) => {
    const currentDate = moment(cur);
    const isCandidate = currentDate[comparisonFn[direction]](date);
    if (!isCandidate) return acc;

    const isCurrentClosest = acc === null ? true : Math.abs(currentDate.diff(date)) < Math.abs(moment(acc).diff(date));
    return isCurrentClosest ? cur : acc;
  }, null);
};

export const isCallMeBackAvailable = (date: Moment) => {
  const weekday = date.isoWeekday();
  const hour = date.hour();
  return weekday >= 1 && weekday <= 6 && hour >= 8 && hour < 19;
};
